// tienda/ReferidosSection.jsx
// Referral program section for SoyTiendaPage.
// Highlights: how it works (3 steps) + interactive income calculator + example scenarios + CTA.
// Positioned before CtaBanner for maximum conversion impact.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Gift, TrendingUp, ChevronRight,
  Share2, UserPlus, HandCoins, Calculator,
} from "lucide-react";
import { brand, WA_HREF, fadeUp } from "../shared/brand";

// ── Constants ────────────────────────────────────────────────────────────────

const SOL_PER_DELIVERY = 1;      // S/ 1 earned per referral's delivery
const MONTHS_DURATION  = 4;      // Program lasts 4 months per referral

const EXAMPLE_SCENARIOS = [
  {
    label:     "Tienda pequeña",
    referees:  2,
    deliveries: 10,
    icon:      "🛍️",
  },
  {
    label:     "Tienda mediana",
    referees:  3,
    deliveries: 20,
    icon:      "📦",
  },
  {
    label:     "Tienda activa",
    referees:  5,
    deliveries: 30,
    icon:      "🚀",
  },
];

const STEPS = [
  {
    icon:  <Share2 className="size-5" />,
    step:  "1",
    title: "Comparte tu código",
    text:  "Encuentra tu código único en tu perfil y compártelo con tiendas o emprendedores que necesiten courier.",
  },
  {
    icon:  <UserPlus className="size-5" />,
    step:  "2",
    title: "Tu referido lo ingresa al registrarse",
    text:  "Al crear su cuenta en Fast Max, ingresan tu código en el campo de referido. Así quedan vinculados a ti.",
  },
  {
    icon:  <HandCoins className="size-5" />,
    step:  "3",
    title: "Ganas S/ 1 por entrega",
    text:  "Por cada entrega que haga tu referido durante 4 meses, recibes S/ 1 directo en tu cuenta.",
  },
];

// ── Main component ────────────────────────────────────────────────────────────

export function ReferidosSection() {
  return (
    <section id="referidos" className="border-b border-slate-100 bg-white scroll-mt-16">
      <div className="mx-auto max-w-7xl px-4 py-12 md:py-16">

        {/* Header */}
        <motion.div {...fadeUp} className="mb-10">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold text-white mb-4"
            style={{ background: brand.accentGold }}
          >
            <Gift className="size-3.5" />
            Programa de referidos
          </div>
          <h2
            className="text-2xl md:text-3xl font-extrabold leading-tight"
            style={{ color: brand.primary }}
          >
            Refiere tiendas y gana mientras duermes
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Ganas <strong>S/ 1 por cada entrega</strong> que haga cada tienda que traigas
            a Fast Max, durante los primeros <strong>4 meses</strong>. Sin límite de referidos,
            sin techo de ganancias.
          </p>
        </motion.div>

        {/* Two-column layout: steps left, calculator right */}
        <div className="grid gap-8 lg:grid-cols-2 mb-10">
          <HowItWorksCard />
          <EarningsCalculator />
        </div>

        {/* Example scenarios */}
        <ExampleScenarios />

        {/* CTA */}
        <motion.div {...fadeUp} className="mt-10 flex flex-col sm:flex-row items-center gap-4">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-sm"
            style={{ background: brand.primary }}
          >
            Crear cuenta y obtener mi código
            <ChevronRight className="size-4" />
          </Link>
          <a
            href={WA_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold hover:underline"
            style={{ color: brand.primary }}
          >
            Tengo preguntas sobre el programa →
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// ── How it works ─────────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <motion.div {...fadeUp} className="rounded-2xl border border-slate-100 bg-slate-50 p-6">
      <div className="flex items-center gap-2 mb-5">
        <Users className="size-5" style={{ color: brand.primary }} />
        <h3 className="text-base font-extrabold text-slate-800">¿Cómo funciona?</h3>
      </div>

      <div className="space-y-5">
        {STEPS.map(s => (
          <div key={s.step} className="flex items-start gap-4">
            {/* Step number circle */}
            <div
              className="shrink-0 size-8 rounded-full flex items-center justify-center text-xs font-extrabold text-white"
              style={{ background: brand.primary }}
            >
              {s.step}
            </div>
            {/* Icon + text */}
            <div>
              <div className="flex items-center gap-1.5 mb-0.5">
                <span style={{ color: brand.primary }}>{s.icon}</span>
                <h4 className="text-sm font-bold text-slate-800">{s.title}</h4>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{s.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Code mockup — shows what the code looks like, not a URL */}
      <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3">
        <p className="text-[11px] text-slate-400 mb-2 font-semibold uppercase tracking-wide">
          Tu código de referido (ejemplo)
        </p>
        <div className="flex items-center justify-between gap-3">
          <p className="text-lg font-mono font-extrabold tracking-widest" style={{ color: brand.primary }}>
            TIENDA-4829
          </p>
          <span className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
            Lo encuentras en tu perfil
          </span>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Tu referido lo ingresa al momento de crear su cuenta en Fast Max.
        </p>
      </div>
    </motion.div>
  );
}

// ── Interactive earnings calculator ──────────────────────────────────────────

function EarningsCalculator() {
  const [referees,   setReferees]   = useState(3);
  const [deliveries, setDeliveries] = useState(20);

  const perDay    = referees * deliveries * SOL_PER_DELIVERY;
  const perMonth  = perDay * 30;
  const total     = perMonth * MONTHS_DURATION;

  return (
    <motion.div
      {...fadeUp}
      className="rounded-2xl border p-6"
      style={{ borderColor: brand.primary + "30", background: brand.primary + "05" }}
    >
      <div className="flex items-center gap-2 mb-5">
        <Calculator className="size-5" style={{ color: brand.primary }} />
        <h3 className="text-base font-extrabold text-slate-800">Calculadora de ganancias</h3>
      </div>

      {/* Sliders */}
      <div className="space-y-5 mb-6">
        <SliderField
          label="Referidos activos"
          value={referees}
          min={1} max={20}
          onChange={setReferees}
          display={`${referees} tienda${referees !== 1 ? "s" : ""}`}
        />
        <SliderField
          label="Entregas por tienda / día"
          value={deliveries}
          min={1} max={100}
          onChange={setDeliveries}
          display={`${deliveries} entregas`}
        />
      </div>

      {/* Results */}
      <div className="grid grid-cols-3 gap-2">
        <ResultPill label="Por día"    value={`S/ ${perDay.toLocaleString()}`}  color={brand.accentGold} />
        <ResultPill label="Por mes"   value={`S/ ${perMonth.toLocaleString()}`} color={brand.accentGreen} />
        <ResultPill label="4 meses"   value={`S/ ${total.toLocaleString()}`}   color={brand.primary} />
      </div>

      <p className="mt-3 text-[11px] text-slate-400 text-center">
        * Estimado basado en S/ 1 por entrega durante 4 meses por referido.
      </p>
    </motion.div>
  );
}

function SliderField({ label, value, min, max, onChange, display }) {
  // Percentage for the colored fill on the range track
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-semibold text-slate-600">{label}</label>
        <span className="text-xs font-extrabold" style={{ color: brand.primary }}>{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, ${brand.primary} ${pct}%, #e2e8f0 ${pct}%)`,
          // WebKit thumb
          WebkitAppearance: "none",
        }}
      />
    </div>
  );
}

function ResultPill({ label, value, color }) {
  return (
    <div
      className="rounded-xl p-3 text-center"
      style={{ background: color + "12" }}
    >
      <p className="text-[10px] font-semibold text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-extrabold" style={{ color }}>{value}</p>
    </div>
  );
}

// ── Example scenarios ─────────────────────────────────────────────────────────

function ExampleScenarios() {
  return (
    <motion.div {...fadeUp}>
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="size-4 text-slate-400" />
        <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide">
          Ejemplos reales
        </h3>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {EXAMPLE_SCENARIOS.map(s => {
          const perDay   = s.referees * s.deliveries * SOL_PER_DELIVERY;
          const perMonth = perDay * 30;
          const total    = perMonth * MONTHS_DURATION;
          return (
            <div
              key={s.label}
              className="rounded-2xl border border-slate-100 bg-slate-50 p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">{s.icon}</span>
                <div>
                  <p className="text-xs font-extrabold text-slate-800">{s.label}</p>
                  <p className="text-[11px] text-slate-400">
                    {s.referees} referidos · {s.deliveries} entregas/día c/u
                  </p>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Por día</span>
                  <span className="font-bold" style={{ color: brand.accentGold }}>
                    S/ {perDay.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Por mes</span>
                  <span className="font-bold" style={{ color: brand.accentGreen }}>
                    S/ {perMonth.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-xs border-t border-slate-200 pt-1 mt-1">
                  <span className="text-slate-600 font-semibold">Total 4 meses</span>
                  <span className="font-extrabold" style={{ color: brand.primary }}>
                    S/ {total.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}